import type { Dictionary } from 'redux-ngrx-entity';
import { createSelector } from 'reselect';
import type { SceneGroup } from '../../core/models/imageAssets';
import { CloudOptions } from '../../core/models/imageAssets';
import type { RootState } from '../reducers';
import type { CatalogOptions } from './models';
import { selectAllSceneGroups } from './reducer';
import { selectPlanetVisualBackgroundFromGroups } from './utils';

const getSceneGroup = (
  entities: Dictionary<SceneGroup>,
  selected: string | null
): SceneGroup | null => {
  return entities[selected ?? ''];
};

export const selectedSceneGroup = createSelector(
  (state: RootState) => state.ImageAsset.entities,
  (state: RootState) => state.ImageAsset.selectedSceneGroupId,
  getSceneGroup
);

export const selectedSideSceneGroup = createSelector(
  (state: RootState) => state.ImageAsset.entities,
  (state: RootState) => state.ImageAsset.selectedSideSceneGroupId,
  getSceneGroup
);

export const selectSceneOptions = createSelector(
  (state: RootState) => state.ImageAsset.options,
  (options) => options
);

const CLOUD_COVERAGE_SUNNY_MAX = 0.335;
const CLOUD_COVERAGE_SUNNY_CLOUDLESS_MAX = 0.665;

const matchesCloudCoverage = (cloudCoverage: number, cloudOptions: CloudOptions[]): boolean => {
  const hasSunny = cloudOptions.includes(CloudOptions.sunny);
  const hasSunnyCloudless = cloudOptions.includes(CloudOptions.sunny_cloudless);
  const hasOvercast = cloudOptions.includes(CloudOptions.overcast);

  if (hasSunny && hasSunnyCloudless && hasOvercast) {
    return cloudCoverage >= 0 && cloudCoverage <= 1;
  }
  if (hasSunny && hasSunnyCloudless) {
    return cloudCoverage >= 0 && cloudCoverage <= CLOUD_COVERAGE_SUNNY_CLOUDLESS_MAX;
  }
  if (hasSunny && hasOvercast) {
    return (
      cloudCoverage <= CLOUD_COVERAGE_SUNNY_MAX ||
      cloudCoverage > CLOUD_COVERAGE_SUNNY_CLOUDLESS_MAX
    );
  }
  if (hasSunnyCloudless && hasOvercast) {
    return cloudCoverage >= CLOUD_COVERAGE_SUNNY_MAX;
  }
  if (hasSunny) {
    return cloudCoverage >= 0 && cloudCoverage <= CLOUD_COVERAGE_SUNNY_MAX;
  }
  if (hasSunnyCloudless) {
    return (
      cloudCoverage > CLOUD_COVERAGE_SUNNY_MAX &&
      cloudCoverage <= CLOUD_COVERAGE_SUNNY_CLOUDLESS_MAX
    );
  }
  return cloudCoverage > CLOUD_COVERAGE_SUNNY_CLOUDLESS_MAX;
};

const sortByAcquiredAtDesc = (a: SceneGroup, b: SceneGroup): number =>
  new Date(b.referenceImage.acquiredAt).getTime() - new Date(a.referenceImage.acquiredAt).getTime();

export const filterSceneGroups = (
  sceneGroups: SceneGroup[],
  options: CatalogOptions,
  showDroneImages: boolean
): SceneGroup[] => {
  const bySource = (item: SceneGroup): boolean => {
    const isDroneImage = item.referenceImage.source === 'drone';
    return (
      options.sources.includes(item.referenceImage.source) && (showDroneImages || !isDroneImage)
    );
  };

  const filtered = sceneGroups.filter(bySource);

  if (options.cloudOptions.length > 0) {
    return filtered
      .filter((item) =>
        matchesCloudCoverage(item.referenceImage.imageCloudCoverage, options.cloudOptions)
      )
      .sort(sortByAcquiredAtDesc);
  }

  return filtered.sort(sortByAcquiredAtDesc);
};

export const filteredSceneGroupsSelector = createSelector(
  (state: RootState) => selectAllSceneGroups(state.ImageAsset),
  (state: RootState) => state.ImageAsset.options,
  (state: RootState) => state.ImageAsset.showDroneImages,
  filterSceneGroups
);

export const filteredSideSceneGroupsSelector = createSelector(
  (state: RootState) => selectAllSceneGroups(state.ImageAsset),
  (state: RootState) => state.ImageAsset.optionsSide,
  (state: RootState) => state.ImageAsset.showDroneImages,
  filterSceneGroups
);

type ReferenceDate = string | Date | undefined;

// Per-consumer factory: each mounted component gets its own memoized instance so the main/side/
// comparison maps (which pass different referenceDates) don't thrash a shared cache-size-1 selector.
export const makeSelectPlanetVisualBackgroundImage = () =>
  createSelector(
    (state: RootState) => selectAllSceneGroups(state.ImageAsset),
    (_state: RootState, referenceDate: ReferenceDate) => referenceDate,
    (_state: RootState, _referenceDate: ReferenceDate, maxAgeDays: number) => maxAgeDays,
    (
      _state: RootState,
      _referenceDate: ReferenceDate,
      _maxAgeDays: number,
      maxCloudPercent: number
    ) => maxCloudPercent,
    selectPlanetVisualBackgroundFromGroups
  );
